"use client"

import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import QRCode from "qrcode"
import { Bounce, toast } from "react-toastify"
import { isAxiosError } from "axios"
import {
  ExternalLink,
  QrCode,
  UserRound,
  Smartphone,
} from "lucide-react"

// adjust import path
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance"

type LaunchStep = "signup" | "subuser" | "scan"

type SubUser = {
  id?: string
  name?: string
  email?: string
  password?: string
  passTemplate?: unknown
  passTemplateId?: string
}

export default function ProgramLaunchPage() {
  const { id } = useParams()
  const [step, setStep] = useState<LaunchStep>("signup")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const [signupQrSrc, setSignupQrSrc] = useState("")
  const [scanQrSrc, setScanQrSrc] = useState("")
  const [signupUrl, setSignupUrl] = useState("")
  const [scanUrl] = useState("https://scan.roaait.com")

  const [subUsers, setSubUsers] = useState<SubUser[]>([])
  const [createdSubUser, setCreatedSubUser] = useState<SubUser | null>(null)

  const [organizationEmail, setOrganizationEmail] = useState("")
  const defaultPassword = "12345678"

  useEffect(() => {
    if (!id) return

    const signup = `${window.location.origin}/CustomerSignup/${id}`
    setSignupUrl(signup)

    QRCode.toDataURL(signup, { width: 220 }, (err, dataUrl) => {
      if (err) {
        console.error("Signup QR generation failed:", err)
      } else {
        setSignupQrSrc(dataUrl)
      }
    })

    QRCode.toDataURL(scanUrl, { width: 220 }, (err, dataUrl) => {
      if (err) {
        console.error("Scan QR generation failed:", err)
      } else {
        setScanQrSrc(dataUrl)
      }
    })
  }, [id, scanUrl])

  async function fetchSubUsers(): Promise<SubUser[]> {
  if (!id) return []

  setLoading(true)
  setError("")

  try {
    const response = await OrganizationAxiosInstance.get(`/PassTemplate/${id}/SubUsers`)
    const users = response.data || []
    setSubUsers(users)

    if (users.length > 0) {
      setCreatedSubUser(users[0])
    }

    return users
  } catch (error) {
    setError("فشل تحميل المستخدمين الفرعيين")
    return []
  } finally {
    setLoading(false)
  }
}

  async function fetchOrganizationInfo() {
    try {
      const response = await OrganizationAxiosInstance.get(`/PassTemplate/${id}`)
      const passTemplate = response.data
      const email = passTemplate?.organization?.email || ""
      setOrganizationEmail(email)
      return email
    } catch {
      return ""
    }
  }

  async function addSubuser(subUser: SubUser) {
    const toastId = toast.loading("💌 جارٍ إضافة مستخدم", {
      position: "top-right",
      theme: "light",
      isLoading: true,
      transition: Bounce,
    })

    try {
      const response = await OrganizationAxiosInstance.post(
        `/Organization/add-subuser`,
        subUser
      )

      if (response.status === 200) {
        toast.update(toastId, {
          render: "👌 تمت إضافة المستخدم بنجاح",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        })
      }
    } catch (error) {
      if (isAxiosError(error)) {
        toast.update(toastId, {
          render: error.response?.data?.error || "فشل إضافة المستخدم",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        })
      }
      throw error
    }
  }

  async function ensureSubUser() {
  setLoading(true)
  setError("")

  try {
    const existingUsers = await fetchSubUsers()

    if (existingUsers.length > 0) {
      setCreatedSubUser(existingUsers[0])
      setStep("subuser")
      return
    }

    let emailToUse = organizationEmail
    if (!emailToUse) {
      emailToUse = await fetchOrganizationInfo()
    }

    if (!emailToUse) {
      throw new Error("لم نجد بريد المنشأة لإنشاء المستخدم الفرعي")
    }

    const newSubUser: SubUser = {
      id: "",
      name: "موظف النقاط",
      email: emailToUse,
      password: defaultPassword,
      passTemplate: undefined,
      passTemplateId: id,
    }

    await addSubuser(newSubUser)

    const refreshedUsers = await fetchSubUsers()

    if (refreshedUsers.length > 0) {
      setCreatedSubUser(refreshedUsers[0])
    } else {
      setCreatedSubUser(newSubUser)
    }

    setStep("subuser")
  } catch (err) {
    console.error(err)
    setError(err instanceof Error ? err.message : "حدث خطأ أثناء تجهيز المستخدم الفرعي")
  } finally {
    setLoading(false)
  }
}

  const visibleSubUser = useMemo(() => {
    if (createdSubUser) return createdSubUser
    if (subUsers.length > 0) return subUsers[0]
    return null
  }, [createdSubUser, subUsers])

  return (
    <main
      dir="rtl"
      className="bg-[linear-gradient(180deg,#f7f8fc_0%,#f5f6fb_45%,#ffffff_100%)] px-4 py-6"
    >
      <div className="mx-auto max-w-md">
        <section className="overflow-hidden rounded-[28px] border border-[#e6e8f2] bg-white p-5 shadow-[0_10px_35px_rgba(17,17,17,0.05)]">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {step === "subuser" && (
                <button
                  type="button"
                  onClick={() => setStep("signup")}
                  className="rounded-xl bg-gray-100 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-200 transition"
                >
                  → السابق
                </button>
              )}
              {step === "scan" && (
                <button
                  type="button"
                  onClick={() => setStep("subuser")}
                  className="rounded-xl bg-gray-100 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-200 transition"
                >
                  → السابق
                </button>
              )}
            </div>

            {/* Top next button — always visible */}
            {step === "signup" && (
              <button
                type="button"
                onClick={ensureSubUser}
                disabled={loading}
                className="flex-shrink-0 flex items-center gap-1.5 rounded-xl bg-[rgb(166,175,216)] px-4 py-2 text-xs font-extrabold text-[#111111] disabled:opacity-50 transition hover:bg-[rgb(145,155,200)]"
              >
                {loading && <svg className="animate-spin h-3.5 w-3.5 text-[#111111]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
                {loading ? "جارٍ التجهيز..." : "التالي ←"}
              </button>
            )}
            {step === "subuser" && (
              <button
                type="button"
                onClick={() => setStep("scan")}
                className="flex-shrink-0 rounded-xl bg-[rgb(166,175,216)] px-4 py-2 text-xs font-extrabold text-[#111111] transition hover:bg-[rgb(145,155,200)]"
              >
                التالي ←
              </button>
            )}
            {step === "scan" && (
              <button
                type="button"
                onClick={() => { if (id) window.location.href = `/Dashboard/Program/${id}`; else window.location.href = `/Dashboard`; }}
                className="flex-shrink-0 rounded-xl bg-[rgb(166,175,216)] px-4 py-2 text-xs font-extrabold text-[#111111] transition hover:bg-[rgb(145,155,200)]"
              >
                فتح البرنامج ←
              </button>
            )}
          </div>

          <div className="mb-6 flex items-center justify-center gap-2">
            <StepDot active={step === "signup"} done={step !== "signup"} />
            <StepDot active={step === "subuser"} done={step === "scan"} />
            <StepDot active={step === "scan"} done={false} />
          </div>

          {error && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === "signup" && (
            <div className="space-y-5">
              <SectionTitle
                title="١) خل العميل يسجل أولًا"
                description="افتح له صفحة التسجيل أو خله يمسح الباركود، وبعدها يحفظ البطاقة في المحفظة."
              />

              {signupQrSrc && (
                <QrCard title="باركود تسجيل العميل" imgSrc={signupQrSrc} />
              )}

              <button
                type="button"
                onClick={() => window.open(signupUrl, "_blank")}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#111111] px-4 py-3 text-sm font-extrabold text-white transition hover:-translate-y-[1px] hover:shadow-lg"
              >
                فتح رابط التسجيل
                <ExternalLink className="h-4 w-4" />
              </button>

              <InfoCard>
                بعد ما العميل يسجل ويحفظ البطاقة، ارجع لهذه الصفحة عشان نجهز لك طريقة إضافة النقاط.
              </InfoCard>

              <button
                type="button"
                onClick={ensureSubUser}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[rgb(166,175,216)] px-4 py-3 text-sm font-extrabold text-[#111111] disabled:opacity-50"
              >
                {loading && <svg className="animate-spin h-4 w-4 text-[#111111]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>}
                {loading ? "جارٍ التجهيز..." : "التالي"}
              </button>
            </div>
          )}

          {step === "subuser" && (
            <div className="space-y-5">
              <SectionTitle
                title="٢) هذا حساب موظف إضافة النقاط"
                description="استخدم هذا الحساب في صفحة المسح حتى تضيف نقاط للعملاء."
              />

              <div className="rounded-2xl border border-[#e5e7eb] bg-[#fafbff] p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#111111]">
                  <UserRound className="h-4 w-4" />
                  بيانات الدخول
                </div>

                <div className="space-y-2 text-sm text-[#5f6678]">
                  <p>
                    <span className="font-bold text-[#111111]">البريد الإلكتروني:</span>{" "}
                    {visibleSubUser?.email || organizationEmail || "—"}
                  </p>
                  <p>
                    <span className="font-bold text-[#111111]">كلمة المرور:</span>{" "}
                    { defaultPassword}
                  </p>
                </div>
              </div>

              <InfoCard>
                تقدر تدخل بهذه البيانات في صفحة المسح من نفس الجوال أو من جوال آخر عند الكاشير.
              </InfoCard>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep("signup")}
                  className="flex-1 rounded-2xl border border-[#dde1ee] bg-white px-4 py-3 text-sm font-bold text-[#111111]"
                >
                  السابق
                </button>

                <button
                  type="button"
                  onClick={() => setStep("scan")}
                  className="flex-1 rounded-2xl bg-[rgb(166,175,216)] px-4 py-3 text-sm font-extrabold text-[#111111]"
                >
                  التالي
                </button>
              </div>
            </div>
          )}

          {step === "scan" && (
            <div className="space-y-5">
              <SectionTitle
                title="٣) الآن أضف النقاط"
                description="افتح صفحة المسح، سجّل دخول موظف النقاط، ثم امسح بطاقة العميل وأضف النقاط."
              />

              {scanQrSrc && (
                <QrCard title="باركود صفحة إضافة النقاط" imgSrc={scanQrSrc} />
              )}

              <button
                type="button"
                onClick={() => window.open(scanUrl, "_blank")}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#111111] px-4 py-3 text-sm font-extrabold text-white transition hover:-translate-y-[1px] hover:shadow-lg"
              >
                فتح صفحة المسح
                <ExternalLink className="h-4 w-4" />
              </button>

              <div className="rounded-2xl border border-[#e5e7eb] bg-[#fafbff] p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[#111111]">
                  <Smartphone className="h-4 w-4" />
                  الطريقة الأسرع
                </div>

                <ol className="space-y-2 text-sm leading-7 text-[#5f6678]">
                  <li>١. خله العميل يسجل ويحفظ البطاقة.</li>
                  <li>٢. افتح صفحة المسح من هذا الجوال أو جوال آخر.</li>
                  <li>٣. سجّل دخول ببيانات موظف النقاط.</li>
                  <li>٤. امسح بطاقة العميل وأضف النقاط مباشرة.</li>
                </ol>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep("subuser")}
                  className="flex-1 rounded-2xl border border-[#dde1ee] bg-white px-4 py-3 text-sm font-bold text-[#111111]"
                >
                  السابق
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (id) {
                      window.location.href = `/Dashboard/Program/${id}`
                    } else {
                      window.location.href = `/Dashboard`
                    }
                  }}
                  className="flex-1 rounded-2xl bg-[rgb(166,175,216)] px-4 py-3 text-sm font-extrabold text-[#111111]"
                >
                  فتح البرنامج
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}

function StepDot({
  active,
  done,
}: {
  active: boolean
  done: boolean
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`h-3 w-3 rounded-full ${
          active
            ? "bg-[#111111]"
            : done
            ? "bg-[rgb(166,175,216)]"
            : "bg-[#d9deec]"
        }`}
      />
      {/* <span
        className={`text-[11px] font-bold ${
          active ? "text-[#111111]" : "text-[#7b8294]"
        }`}
      >
        {label}
      </span> */}
    </div>
  )
}

function SectionTitle({
  title,
  description,
}: {
  title: string
  description: string
}) {
  return (
    <div>
      <h2 className="text-xl font-black text-[#111111]">{title}</h2>
      <p className="mt-2 text-sm leading-7 text-[#5f6678]">{description}</p>
    </div>
  )
}

function QrCard({
  title,
  imgSrc,
}: {
  title: string
  imgSrc: string
}) {
  return (
    <div className="rounded-2xl border border-[#e5e7eb] bg-[#fafbff] p-4 text-center">
      <div className="mb-3 flex items-center justify-center gap-2 text-sm font-bold text-[#111111]">
        <QrCode className="h-4 w-4" />
        {title}
      </div>
      <img
        src={imgSrc}
        alt={title}
        className="mx-auto h-[220px] w-[220px]"
      />
    </div>
  )
}

function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[#dbe3f6] bg-[#f7f9ff] px-4 py-3 text-sm leading-7 text-[#44506b]">
      {children}
    </div>
  )
}